/** Jurisdiction-neutral accounting-governance payloads; policy values remain content. */
export const ACCOUNTING_ASSET_CLASSES = Object.freeze([
    'EQUITY',
    'DEBT',
    'CASH',
    'RECEIVABLE',
]);
export const FAIR_VALUE_HIERARCHIES = Object.freeze(['LEVEL_1', 'LEVEL_2', 'LEVEL_3']);
const isObject = (value) => value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);
const date = (value) => {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value))
        return false;
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) && parsed.toISOString() === value;
};
const string = (value) => typeof value === 'string' && value.trim().length > 0 && value.length <= 256;
const ids = (value) => Array.isArray(value) &&
    value.length > 0 &&
    value.length <= 128 &&
    value.every(string) &&
    new Set(value).size === value.length;
const decimal = (value) => typeof value === 'string' && value.length <= 64 && /^\d+(\.\d{1,18})?$/.test(value);
const validate = (value, prefix, allowed) => !isObject(value)
    ? [`${prefix} payload must be an object`]
    : Object.keys(value)
        .filter((key) => !allowed.includes(key) || ['__proto__', 'constructor', 'prototype'].includes(key))
        .map((key) => `${prefix}.${key} is not allowed`);
export function validateAccountingClassificationPolicy(value) {
    return (validate(value, 'classificationPolicy', ['classificationRules', 'independentApprovalRequired'])
        .length === 0 &&
        isObject(value) &&
        Array.isArray(value.classificationRules) &&
        value.classificationRules.length > 0 &&
        value.classificationRules.every((rule) => validate(rule, 'classificationRule', ['assetClass', 'classifications', 'measurementBases'])
            .length === 0 &&
            isObject(rule) &&
            ACCOUNTING_ASSET_CLASSES.includes(rule.assetClass) &&
            ids(rule.classifications) &&
            ids(rule.measurementBases)) &&
        new Set(value.classificationRules.map((rule) => rule.assetClass)).size ===
            value.classificationRules.length &&
        typeof value.independentApprovalRequired === 'boolean');
}
export function validateExpectedCreditLossPolicy(value) {
    if (validate(value, 'eclPolicy', [
        'stages',
        'allowedTransitions',
        'models',
        'completeInputsRequired',
    ]).length > 0 ||
        !isObject(value) ||
        !ids(value.stages) ||
        !Array.isArray(value.allowedTransitions) ||
        value.allowedTransitions.length === 0 ||
        !Array.isArray(value.models) ||
        value.models.length === 0 ||
        !value.models.every((model) => validate(model, 'eclModel', ['modelId', 'versions']).length === 0 &&
            isObject(model) &&
            string(model.modelId) &&
            ids(model.versions)) ||
        new Set(value.models.map((model) => model.modelId)).size !==
            value.models.length ||
        typeof value.completeInputsRequired !== 'boolean')
        return false;
    const stages = value.stages;
    return value.allowedTransitions.every((transition) => validate(transition, 'eclTransition', ['from', 'to']).length === 0 &&
        isObject(transition) &&
        stages.includes(transition.from) &&
        stages.includes(transition.to));
}
export function validateFairValuePolicy(value) {
    if (validate(value, 'fairValuePolicy', ['measurementRules']).length > 0 ||
        !isObject(value) ||
        !Array.isArray(value.measurementRules) ||
        value.measurementRules.length === 0)
        return false;
    return (value.measurementRules.every((rule) => validate(rule, 'fairValueRule', [
        'hierarchy',
        'observableEvidenceRequired',
        'unobservableEvidenceRequired',
        'uncertaintyDisclosureRequired',
    ]).length === 0 &&
        isObject(rule) &&
        FAIR_VALUE_HIERARCHIES.includes(rule.hierarchy) &&
        typeof rule.observableEvidenceRequired === 'boolean' &&
        typeof rule.unobservableEvidenceRequired === 'boolean' &&
        typeof rule.uncertaintyDisclosureRequired === 'boolean') &&
        new Set(value.measurementRules.map((rule) => rule.hierarchy))
            .size === value.measurementRules.length);
}
export function validateAccountingDisclosurePolicy(value) {
    return (validate(value, 'disclosurePolicy', ['immutableOriginalRequired', 'correctionLineageRequired'])
        .length === 0 &&
        isObject(value) &&
        typeof value.immutableOriginalRequired === 'boolean' &&
        typeof value.correctionLineageRequired === 'boolean');
}
export function validateAccountingClassificationPayload(value) {
    const e = validate(value, 'classification', [
        'assetClass',
        'classification',
        'measurementBasis',
        'judgmentEvidenceIds',
        'preparedBy',
    ]);
    if (!isObject(value))
        return e;
    if (!ACCOUNTING_ASSET_CLASSES.includes(value.assetClass))
        e.push('classification.assetClass is invalid');
    if (!string(value.classification))
        e.push('classification.classification is required');
    if (!string(value.measurementBasis))
        e.push('classification.measurementBasis is required');
    if (!ids(value.judgmentEvidenceIds))
        e.push('classification.judgmentEvidenceIds is required');
    if (!string(value.preparedBy))
        e.push('classification.preparedBy is required');
    return e;
}
export function validateExpectedCreditLossRunPayload(value) {
    const e = validate(value, 'ECL', [
        'modelId',
        'modelVersion',
        'inputEvidenceIds',
        'riskStageBefore',
        'riskStageAfter',
        'completenessCheckId',
        'journalEntryId',
        'exposure',
        'lossRate',
        'eventTime',
    ]);
    if (!isObject(value))
        return e;
    for (const key of [
        'modelId',
        'modelVersion',
        'riskStageBefore',
        'riskStageAfter',
        'completenessCheckId',
        'journalEntryId',
    ])
        if (!string(value[key]))
            e.push(`ECL.${key} is required`);
    if (!ids(value.inputEvidenceIds))
        e.push('ECL.inputEvidenceIds is required');
    if (!decimal(value.exposure) || !decimal(value.lossRate))
        e.push('ECL.decimals are invalid');
    if (!date(value.eventTime))
        e.push('ECL.eventTime is invalid');
    return e;
}
export function validateFairValueMeasurementPayload(value) {
    const e = validate(value, 'fairValue', [
        'hierarchy',
        'observableInputEvidenceIds',
        'unobservableInputEvidenceIds',
        'uncertaintyDisclosure',
        'eventTime',
    ]);
    if (!isObject(value))
        return e;
    if (!FAIR_VALUE_HIERARCHIES.includes(value.hierarchy))
        e.push('fairValue.hierarchy is invalid');
    if (!Array.isArray(value.observableInputEvidenceIds) ||
        !value.observableInputEvidenceIds.every(string))
        e.push('fairValue.observableInputEvidenceIds is invalid');
    if (!Array.isArray(value.unobservableInputEvidenceIds) ||
        !value.unobservableInputEvidenceIds.every(string))
        e.push('fairValue.unobservableInputEvidenceIds is invalid');
    if (!date(value.eventTime))
        e.push('fairValue.eventTime is invalid');
    return e;
}
export function validateAccountingDisclosurePayload(value) {
    const e = validate(value, 'disclosure', ['disclosureVersion', 'periodEnd', 'evidenceIds']);
    if (!isObject(value))
        return e;
    if (!string(value.disclosureVersion))
        e.push('disclosure.disclosureVersion is required');
    if (!date(value.periodEnd))
        e.push('disclosure.periodEnd is invalid');
    if (!ids(value.evidenceIds))
        e.push('disclosure.evidenceIds is required');
    return e;
}
export function validateAccountingRestatementLineage(value) {
    const e = validate(value, 'restatement', ['supersedesResultId', 'restatementReason']);
    if (!isObject(value))
        return e;
    if (!string(value.supersedesResultId))
        e.push('restatement.supersedesResultId is required');
    if (!string(value.restatementReason))
        e.push('restatement.restatementReason is required');
    return e;
}
